import React, {useReducer, useEffect, useContext, memo, useState} from 'react';
import classNames from 'classnames';
import Select from 'react-select';
import BusinessInvoicesContext, {
  BussinessOrg,
  CompanyInvoiceSearch,
  InvoiceCountList,
} from 'app/Components/Page/BusinessInvoicesPage/BusinessInvoicesContext';
import DeniedMessageBox from 'app/Components/Shared/DeniedMessageBox/DeniedMessageBox';
import Config from 'app/Config';
import {getBansForOrg} from '../BusinessReportBlock/BusinessReportBlockSlice';
import { useDispatch } from 'react-redux';

const phoneRegex = /^0?(([23489]{1}\d{7})|[57]{1}\d{8})$/;

interface InvoiceOrigin {
  name: string;
  selected: boolean;
  disabled: boolean;
}

const DefaultInvoiceOrigins: InvoiceOrigin[] = [
  {
    name: `${window.Config.brand_Name_HE}`,
    selected: true,
    disabled: false,
  },
  {
    name: `נטוויז׳ן`,
    disabled: false,
    selected: true,
  },
];

type StateErrors = {
  filter?: string;
  filterValue?: string;
};

type State = {
  search: CompanyInvoiceSearch;
  invoiceOriginList: InvoiceOrigin[];
  invoiceShowOriginList: boolean;
  errors: StateErrors | null;
  showErrors: boolean;
  showEmpty: boolean;
  disabled: boolean;
};

type Action =
  | {type: 'init'; org: BussinessOrg}
  | {type: 'invoiceOrigin'; invoiceOrigin: InvoiceOrigin}
  | {type: 'invoiceCount'; invoiceCount: number}
  | {type: 'filterByBanPsid'}
  | {type: 'filterBySubscriber'}
  | {type: 'filterValue'; value: string}
  | {type: 'searchButton'; showErrors: boolean};

const emptySearch: CompanyInvoiceSearch = {
  invoiceOrigin: null,
  invoiceCount: 1,
  filterByBanPsid: false,
  filterBySubscriber: false,
  filterValue: '',
};

const initialState: State = {
  search: emptySearch,
  invoiceShowOriginList: false,
  invoiceOriginList: JSON.parse(JSON.stringify(DefaultInvoiceOrigins)),
  disabled: true,
  errors: null,
  showErrors: false,
  showEmpty: false,
};

const BusinessInvoicesSearchReducer = (state: State, action: Action): State => {
  // every interaction should remove errors
  let showErrors = false;
  switch (action.type) {
    case 'init':
      state.invoiceOriginList[0].selected = action.org.cellInd;
      state.invoiceOriginList[1].selected = action.org.netvInd;
      state = {...state, invoiceShowOriginList: action.org.cellInd && action.org.netvInd};
      break;
    case 'invoiceOrigin':
      action.invoiceOrigin.selected = !action.invoiceOrigin.selected;
      break;
    case 'invoiceCount':
      if (action.invoiceCount > 6 && !state.search.filterBySubscriber) {
        state.search.filterByBanPsid = true;
      }
      state = {...state, search: {...state.search, invoiceCount: action.invoiceCount}};
      break;
    case 'filterByBanPsid':
      state = {
        ...state,
        search: {
          ...state.search,
          filterByBanPsid: !state.search.filterByBanPsid,
          filterBySubscriber: false,
          filterValue: '',
        },
      };
      break;

    case 'filterBySubscriber':
      state = {
        ...state,
        search: {
          ...state.search,
          filterBySubscriber: !state.search.filterBySubscriber,
          filterByBanPsid: false,
          filterValue: '',
        },
      };
      break;
    case 'filterValue':
      state = {...state, search: {...state.search, filterValue: action.value}};
      break;
    case 'searchButton':
      showErrors = action.showErrors;
      state = {...state, showEmpty: !showErrors};
      break;
  }

  // InvoiceOrigin determination
  const p0 = state.invoiceOriginList[0].selected;
  const p1 = state.invoiceOriginList[1].selected;
  state.search.invoiceOrigin = p0 && p1 ? 2 : p1 ? 1 : p0 ? 0 : null;

  // Filter errors
  let errors: StateErrors = null;
  const isLongMonths = state.search.invoiceCount > 6;
  const isFilterValueEmpty = state.search.filterValue == '';
  const isNetvision = state.search.invoiceOrigin == 1;
  state.search.filterBySubscriber = isNetvision ? false : state.search.filterBySubscriber;

  if (isLongMonths && state.search.filterBySubscriber && isFilterValueEmpty) {
    errors = {filterValue: 'מחכים למספר המנוי'};
  } else if (isLongMonths && state.search.filterByBanPsid && isFilterValueEmpty) {
    errors = {filterValue: 'מחכים למספר הלקוח'};
  } else if (isLongMonths && !state.search.filterByBanPsid && !state.search.filterBySubscriber) {
    errors = isNetvision ? {filter: 'נא לבחור סינון לפי מספר לקוח'} : {filter: 'נא לבחור סינון לפי מספר לקוח או מספר מנוי'};
  } else if (!isFilterValueEmpty && state.search.filterBySubscriber && !phoneRegex.test(state.search.filterValue)) {
    errors = {filterValue: 'מספר מנוי לא תקין'};
  }

  // Button Disabled
  const disabled = state.search.invoiceOrigin == null;

  return {...state, disabled, showErrors, errors};
};

interface SearchProps {
  org: BussinessOrg;
  empty: boolean;
  onSearch: (opts: CompanyInvoiceSearch) => void;
}

const InvoicesSearch = ({org, onSearch, empty}: SearchProps) => {
  const [{disabled, invoiceShowOriginList, invoiceOriginList, search, errors, showErrors, showEmpty}, dispatch] =
    useReducer(BusinessInvoicesSearchReducer, initialState);

  const reduxDispatch = useDispatch();

  const [banOptions, setBanOptions] = useState<{ value: string; label: string }[]>([]);

  const fetchBanOptions = async () => {
    try {
      const res: any = await reduxDispatch(getBansForOrg(org.idNumber));

      const options =
        res?.payload?.Body?.map((item: any) => ({
          value: item.banId,
          label: item.fullName
            ? `${item.banId} - ${item.fullName}`
            : `${item.banId}`,

        })) || [];

      setBanOptions(options);
    } catch (err) {
      console.error('Error:', err);
    }
  };

  useEffect(() => {
    if (org) {
      fetchBanOptions();
    }
  }, [org]);

  useEffect(() => dispatch({type: 'init', org}), [org]);

  const selectedBan = banOptions.find(o => o.value === search.filterValue) || null;

  return (
    <div className='BusinessInvoicesSearch'>
      {invoiceShowOriginList && (
        <div className='BusinessInvoicesSearch__block'>
          <div className='BusinessInvoicesSearch__label'>בחירת סוג חשבון</div>
          <div className='BusinessInvoicesSearch__buttons'>
            {invoiceOriginList.map((invoiceOrigin, i) => (
              <button
                key={i}
                disabled={invoiceOrigin.disabled}
                className={classNames('BusinessInvoicesSearch__provider', {selected: invoiceOrigin.selected})}
                onClick={() => dispatch({type: 'invoiceOrigin', invoiceOrigin})}>
                {invoiceOrigin.name}
              </button>
            ))}
          </div>
        </div>
      )}
      <div className='BusinessInvoicesSearch__block'>
        <div className='BusinessInvoicesSearch__label'>כמה חודשים אחרונים להציג?</div>
        <div className='BusinessInvoicesSearch__buttons'>
          {InvoiceCountList.map((invoiceCount, i) => (
            <button
              className={classNames('BusinessInvoicesSearch__months', {selected: search.invoiceCount === invoiceCount})}
              onClick={() => dispatch({type: 'invoiceCount', invoiceCount})}
              key={i}>
              {invoiceCount}
            </button>
          ))}
        </div>
      </div>
      <div className='BusinessInvoicesSearch__block'>
        <div className='BusinessInvoicesSearch__label'>סינון לפי</div>
        <div className='BusinessInvoicesSearch__buttons'>
          <button
            className={classNames('BusinessInvoicesSearch__type', {selected: search.filterByBanPsid})}
            onClick={() => dispatch({type: 'filterByBanPsid'})}>
            מספר לקוח
          </button>
          <button
            className={classNames('BusinessInvoicesSearch__type', {
              selected: search.filterBySubscriber,
              hidden: search.invoiceOrigin === 1,
            })}
            onClick={() => dispatch({type: 'filterBySubscriber'})}>
            מספר מנוי
          </button>
        </div>
        {showErrors && errors?.filter && (
          <div className='BusinessInvoicesSearch__block__error'>
            <span>{errors.filter}</span>
          </div>
        )}
      </div>

      {search.filterByBanPsid && (
        <div className='BusinessInvoicesSearch__block'>
          <div className='BusinessInvoicesSearch__type__input'>
            <label>מספר לקוח</label>
            <Select
              options={banOptions}
              value={selectedBan}
              isLoading={!banOptions.length}
              placeholder="בחר מספר לקוח"
              onChange={(option: any) =>
                dispatch({type: 'filterValue', value: option ? option.value : ''})
              }
              isClearable
            />
          </div>

          {showErrors && errors?.filterValue && (
            <div className='BusinessInvoicesSearch__block__error'>
              <span>{errors.filterValue}</span>
            </div>
          )}
        </div>
      )}

      {search.filterBySubscriber && (
        <div className='BusinessInvoicesSearch__block'>
          <div className='BusinessInvoicesSearch__type__input'>
            <label>מספר מנוי</label>
            <input
              type='number'
              onKeyPress={(e) => 'Ee+-.'.indexOf(e.key) >= 0 && e.preventDefault()}
              onInput={(e) => (e.currentTarget.value = e.currentTarget.value.slice(0, 10))}
              value={search.filterValue}
              onChange={(e) =>
                dispatch({type: 'filterValue', value: e.currentTarget.value})
              }
            />
          </div>
          {showErrors && errors?.filterValue && (
            <div className='BusinessInvoicesSearch__block__error'>
              <span>{errors.filterValue}</span>
            </div>
          )}
        </div>
      )}

      {empty && showEmpty && (
        <div className='BusinessInvoicesSearch__error'>
          <span>לא מצאנו חשבוניות לפי הסינונים שהגדרתם.</span>
          <span>כדאי לעדכן אותם ולנסות שוב</span>
        </div>
      )}

      <button
        className='BusinessInvoicesSearch__submit'
        disabled={disabled}
        onClick={() => {
          dispatch({type: 'searchButton', showErrors: errors != null});
          onSearch(errors == null ? {...search} : null);
        }}>
        חיפוש
      </button>
    </div>
  );
};

const MemoizedInvoicesSearch = memo(InvoicesSearch);

const BusinessInvoicesSearch = () => {
  const [{selected, empty, collapsed}, dispatch] = useContext(BusinessInvoicesContext);
  if (selected == null) return null;
  return selected.cellInd || selected.netvInd ? (
    <div className={classNames('BusinessInvoicesSearchWrapper', {collapsed})}>
      <MemoizedInvoicesSearch empty={empty} org={selected} onSearch={(search) => dispatch({type: 'search', search})} />
    </div>
  ) : (
    <div className='BusinessInvoicesSearch__denied'>
      <DeniedMessageBox
        text='מצטערים אין לך הרשאה לבצע את הפעולה עבור חברה זו'
        subText={`חיוג ${Config.businessCallNumber} לעדכון הרשאה`}
        asBtn={true}
      />
    </div>
  );
};

export default memo(BusinessInvoicesSearch);
